<script setup lang="ts">
import { inject } from 'vue';
import { timeFormat } from '@/utils';
import { useI18n } from 'vue-i18n';
import { useRuntimeBranding } from '@/composables/runtimeBranding';

import { LinkButton } from '@thunderbirdops/services-ui';
import { apiUrlKey, dayjsKey } from '@/keys';
import { Appointment, Attendee, Slot } from '@/models';
import { PhDownloadSimple, PhConfetti } from '@phosphor-icons/vue';

const { t } = useI18n();
const { branding } = useRuntimeBranding();

const dj = inject(dayjsKey);
const apiUrl = inject(apiUrlKey);

// component properties
interface Props {
  selectedEvent: Appointment & Slot,
  attendee: Attendee,
  requested: boolean, // True if we are requesting a booking, false if already confirmed
}
const props = defineProps<Props>();

const heading = props.requested
  ? t('info.bookingSuccessfullyRequested')
  : t('info.bookingSuccessfullyConfirmed');

const description = props.requested
  ? t('text.hostHasBeenNotified')
  : t('text.timeHasBeenConfirmed', {'email': props.attendee.email});

const date = dj(props.selectedEvent.start).format('ddd') + ', '
  + dj(props.selectedEvent.start).format('MMM D') + ' from '
  + dj(props.selectedEvent.start).format(timeFormat()) + ' – '
  + dj(props.selectedEvent.start).add(props.selectedEvent.duration, 'minutes').format(timeFormat())
  + ' (' + dj.tz.guess() + ')';

const downloadUrl = `${apiUrl}/apmt/serve/ics/${props.selectedEvent.slug}/${props.selectedEvent.id}`;

</script>

<template>
  <div class="booking-details">
    <div class="heading">
      <ph-confetti />
      {{ heading }}
    </div>
    <p>{{ description }}</p>
    <div class="info">
      <div class="logo">
        <img :src="branding.header_logo_url" :alt="branding.logo_alt" />
      </div>
      <div>
        {{ date }}
        <br />
        {{ t('text.virtualMeetingWith', {name: attendee.name}) }}
      </div>
    </div>
    <div class="actions">
      <link-button :href="downloadUrl">
        <template #iconLeft>
          <ph-download-simple />
        </template>
        {{ t('label.downloadTheIcsFile') }}
      </link-button>
    </div>
  </div>
</template>

<style scoped>
@import '@/assets/styles/custom-media.pcss';

.booking-details {
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  max-width: 48rem;

  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  background-color: var(--colour-neutral-base);
  font-family: Inter, sans-serif;

  .heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  
    color: var(--colour-ti-highlight);
    font-size: 1.5rem;
    text-transform: capitalize;
  
    svg {
      fill: var(--colour-ti-highlight);
    }
  }

  p {
    color: var(--colour-ti-base);
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    font-size: 1.25rem;
    color: var(--colour-ti-black);

    .logo {
      flex-shrink: 0;
      align-self: center;

      img {
        width: 148px;
        height: auto;
        object-fit: contain;
        display: block;
      }
    }
  }

  .actions {
    display: flex;

    a {
      padding: 0;
      color: var(--colour-ti-highlight);
      font-size: .75rem;
    }

    :deep(.base.link.filled) .icon,
    :deep(.base.link.filled) .icon svg {
      width: 16px !important;
      height: 16px !important;
    }
  }
}
@media (--sm) {
  .booking-details .info {
    flex-direction: row;
  }
}
</style>
